<template>
  <div class="min-h-screen bg-slate-900">
    <AdminLayout>
      <div class="p-8">
        <!-- Header -->
        <div class="mb-8">
          <h1 class="text-3xl font-bold text-white mb-2">AI-Powered Roadmap Management</h1>
          <p class="text-slate-400">Manage and update the product roadmap using AI agents</p>
        </div>

        <!-- AI Analysis Section -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <!-- Feature Request Analysis -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-4">AI Feature Analysis</h2>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Total Requests</span>
                <span class="text-emerald-400 font-semibold">{{ featureRequests.length }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Popular Categories</span>
                <span class="text-blue-400 font-semibold">{{ popularCategories.length }}</span>
              </div>
              <button 
                @click="analyzeFeatureRequests"
                :disabled="isAnalyzing"
                class="w-full btn-primary-modern disabled:opacity-50"
              >
                <svg v-if="isAnalyzing" class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isAnalyzing ? 'Analyzing...' : 'Run AI Analysis' }}
              </button>
            </div>
          </div>

          <!-- Priority Suggestions -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-4">AI Priority Suggestions</h2>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-slate-300">High Priority</span>
                <span class="text-red-400 font-semibold">{{ highPriorityFeatures.length }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-300">Medium Priority</span>
                <span class="text-yellow-400 font-semibold">{{ mediumPriorityFeatures.length }}</span>
              </div>
              <button 
                @click="generatePriorities"
                :disabled="isGeneratingPriorities"
                class="w-full btn-primary-modern disabled:opacity-50"
              >
                <svg v-if="isGeneratingPriorities" class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isGeneratingPriorities ? 'Generating...' : 'Generate Priorities' }}
              </button>
            </div>
          </div>
        </div>

        <!-- AI Suggestions -->
        <div v-if="aiSuggestions.length > 0" class="mb-8">
          <h2 class="text-2xl font-bold text-white mb-6">AI-Generated Feature Suggestions</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="suggestion in aiSuggestions" 
              :key="suggestion.title"
              class="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6"
            >
              <div class="flex items-start justify-between mb-4">
                <h3 class="text-lg font-semibold text-white">{{ suggestion.title }}</h3>
                <span 
                  :class="{
                    'bg-red-500/20 text-red-400': suggestion.priority === 'high',
                    'bg-yellow-500/20 text-yellow-400': suggestion.priority === 'medium',
                    'bg-blue-500/20 text-blue-400': suggestion.priority === 'low'
                  }"
                  class="px-2 py-1 rounded-full text-xs font-medium"
                >
                  {{ suggestion.priority }}
                </span>
              </div>
              <p class="text-slate-300 text-sm mb-4">{{ suggestion.description }}</p>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-xs">{{ suggestion.category }}</span>
                <span class="text-slate-400 text-xs">{{ suggestion.estimatedEffort }}</span>
              </div>
              <div class="mt-4 flex space-x-2">
                <button 
                  @click="addToRoadmap(suggestion)"
                  class="btn-outline text-xs"
                >
                  Add to Roadmap
                </button>
                <button 
                  @click="dismissSuggestion(suggestion)"
                  class="btn-secondary text-xs"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Roadmap Management -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Completed Features -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-4">✅ Completed Features</h2>
            <div class="space-y-3">
              <div 
                v-for="feature in completedFeatures" 
                :key="feature.id"
                class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4"
              >
                <h3 class="text-white font-medium mb-2">{{ feature.title }}</h3>
                <p class="text-slate-400 text-sm mb-2">{{ feature.description }}</p>
                <div class="flex items-center justify-between">
                  <span class="text-emerald-400 text-xs">{{ feature.category }}</span>
                  <span class="text-slate-500 text-xs">{{ feature.completedDate }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- In Progress Features -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-4">🔄 In Progress</h2>
            <div class="space-y-3">
              <div 
                v-for="feature in inProgressFeatures" 
                :key="feature.id"
                class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4"
              >
                <h3 class="text-white font-medium mb-2">{{ feature.title }}</h3>
                <p class="text-slate-400 text-sm mb-2">{{ feature.description }}</p>
                <div class="mb-2">
                  <div class="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span>Progress</span>
                    <span>{{ feature.progress }}%</span>
                  </div>
                  <div class="w-full bg-slate-600 rounded-full h-2">
                    <div 
                      class="bg-blue-500 h-2 rounded-full"
                      :style="{ width: feature.progress + '%' }"
                    ></div>
                  </div>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-blue-400 text-xs">{{ feature.category }}</span>
                  <span class="text-slate-500 text-xs">{{ feature.estimatedCompletion }}</span>
                </div>
                <div class="mt-2 flex space-x-2">
                  <button 
                    @click="updateProgress(feature)"
                    class="btn-outline text-xs"
                  >
                    Update Progress
                  </button>
                  <button 
                    @click="markCompleted(feature)"
                    class="btn-success text-xs"
                  >
                    Mark Complete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Planned Features -->
          <div class="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-4">📋 Planned Features</h2>
            <div class="space-y-3">
              <div 
                v-for="feature in plannedFeatures" 
                :key="feature.id"
                class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4"
              >
                <h3 class="text-white font-medium mb-2">{{ feature.title }}</h3>
                <p class="text-slate-400 text-sm mb-2">{{ feature.description }}</p>
                <div class="flex items-center justify-between">
                  <span class="text-orange-400 text-xs">{{ feature.category }}</span>
                  <span class="text-slate-500 text-xs">{{ feature.plannedDate }}</span>
                </div>
                <div class="mt-2 flex space-x-2">
                  <button 
                    @click="moveToInProgress(feature)"
                    class="btn-outline text-xs"
                  >
                    Start Development
                  </button>
                  <button 
                    @click="editFeature(feature)"
                    class="btn-secondary text-xs"
                  >
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline Generation -->
        <div class="mt-8 bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <h2 class="text-xl font-semibold text-white mb-4">AI Timeline Generation</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div class="flex items-center space-x-3">
              <input 
                v-model="timelineOptions.teamCapacity" 
                type="checkbox" 
                id="teamCapacity"
                class="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
              >
              <label for="teamCapacity" class="text-slate-300">Consider Team Capacity</label>
            </div>
            <div class="flex items-center space-x-3">
              <input 
                v-model="timelineOptions.dependencies" 
                type="checkbox" 
                id="dependencies"
                class="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
              >
              <label for="dependencies" class="text-slate-300">Analyze Dependencies</label>
            </div>
            <div class="flex items-center space-x-3">
              <input 
                v-model="timelineOptions.resourceAllocation" 
                type="checkbox" 
                id="resourceAllocation"
                class="rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
              >
              <label for="resourceAllocation" class="text-slate-300">Resource Allocation</label>
            </div>
          </div>
          <button 
            @click="generateTimeline"
            :disabled="isGeneratingTimeline"
            class="btn-primary-modern disabled:opacity-50"
          >
            <svg v-if="isGeneratingTimeline" class="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isGeneratingTimeline ? 'Generating Timeline...' : 'Generate AI Timeline' }}
          </button>
        </div>

        <!-- Generated Timeline -->
        <div v-if="generatedTimeline" class="mt-8 bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <h2 class="text-xl font-semibold text-white mb-6">AI-Generated Timeline</h2>
          <div class="space-y-6">
            <div 
              v-for="phase in generatedTimeline.phases" 
              :key="phase.phase"
              class="bg-slate-700/50 border border-slate-600/50 rounded-xl p-6"
            >
              <h3 class="text-lg font-semibold text-white mb-4">{{ phase.phase }} ({{ phase.duration }})</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div v-for="feature in phase.features" :key="feature.title" class="bg-slate-600/50 rounded-lg p-3">
                  <h4 class="text-white font-medium">{{ feature.title }}</h4>
                  <p class="text-slate-400 text-sm">{{ feature.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  </div>
</template>

<script setup lang="ts">
import AdminLayout from '@/components/admin/AdminLayout.vue'
import { computed, ref } from 'vue'

// State
const isAnalyzing = ref(false)
const isGeneratingPriorities = ref(false)
const isGeneratingTimeline = ref(false)
const aiSuggestions = ref([])
const generatedTimeline = ref(null)

// Timeline options
const timelineOptions = ref({
  teamCapacity: true,
  dependencies: true,
  resourceAllocation: true
})

// Mock feature requests (in real app, this would come from database)
const featureRequests = ref([
  { title: 'Mobile App', category: 'mobile-app', description: 'Native mobile applications' },
  { title: 'Advanced Analytics', category: 'analytics', description: 'Comprehensive analytics dashboard' },
  { title: 'QuickBooks Integration', category: 'integrations', description: 'QuickBooks API integration' },
  { title: 'Real-time Notifications', category: 'core', description: 'Push notifications for updates' },
  { title: 'AI Tax Optimization', category: 'ai-features', description: 'AI-powered tax optimization' }
])

// Roadmap data (same as in RoadmapPage)
const completedFeatures = ref([
  {
    id: 1,
    title: 'User Authentication & Registration',
    description: 'Secure login/register system with role-based access (contractor, company, admin)',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 2,
    title: 'Contractor Dashboard',
    description: 'Main dashboard with job marketplace, scheduling, and financial overview',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 3,
    title: 'Company Dashboard',
    description: 'Company portal for job posting, crew management, and operations',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 4,
    title: 'Job Marketplace',
    description: 'Job posting, browsing, and application system for contractors',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 5,
    title: 'Basic AI Messaging',
    description: 'AI-powered chat assistant for general queries and support',
    category: 'AI',
    completedDate: 'Q2 2024'
  },
  {
    id: 6,
    title: 'Payment Processing',
    description: 'Digital payment system with timesheet integration',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 7,
    title: 'Expense Tracking',
    description: 'Mileage, per diem, fuel, and PPE expense management',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 8,
    title: 'Tax Calculation Tools',
    description: 'Quarterly tax estimates and year-end reporting for contractors',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 9,
    title: 'Safety & Compliance',
    description: 'Safety incident tracking and compliance reporting system',
    category: 'Safety',
    completedDate: 'Q3 2024'
  },
  {
    id: 10,
    title: 'Scheduling System',
    description: 'Calendar integration with rotation logic (2 weeks on/1 off)',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 11,
    title: 'Timesheets',
    description: 'Digital timesheet submission and approval workflow',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 12,
    title: 'Bank Account Management',
    description: 'Multiple bank account linking and transaction tracking',
    category: 'Financial',
    completedDate: 'Q3 2024'
  },
  {
    id: 13,
    title: 'Profile Management',
    description: 'User profile customization and document upload',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 14,
    title: 'Settings & Preferences',
    description: 'User settings, notifications, and account preferences',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 15,
    title: 'Knowledge Hub',
    description: 'Industry resources, guides, and educational content',
    category: 'Content',
    completedDate: 'Q4 2024'
  },
  {
    id: 16,
    title: 'Store & Marketplace',
    description: 'Equipment and supplies marketplace for industry professionals',
    category: 'Commerce',
    completedDate: 'Q4 2024'
  },
  {
    id: 17,
    title: 'Support System',
    description: 'Customer support ticketing and help center',
    category: 'Support',
    completedDate: 'Q4 2024'
  },
  {
    id: 18,
    title: 'Admin Panel',
    description: 'Comprehensive admin dashboard for platform management',
    category: 'Admin',
    completedDate: 'Q4 2024'
  }
])

const inProgressFeatures = ref([
  {
    id: 19,
    title: 'Advanced AI Tax Optimization',
    description: 'AI-powered tax optimization and deduction recommendations',
    category: 'AI',
    progress: 85,
    estimatedCompletion: 'Q1 2025'
  },
  {
    id: 20,
    title: 'AI Agentic Framework',
    description: 'Autonomous AI agents for complex task execution and decision making',
    category: 'AI',
    progress: 70,
    estimatedCompletion: 'Q1 2025'
  },
  {
    id: 21,
    title: 'Mobile App Development',
    description: 'Native mobile applications for iOS and Android field operations',
    category: 'Mobile',
    progress: 60,
    estimatedCompletion: 'Q2 2025'
  },
  {
    id: 22,
    title: 'Advanced Analytics Dashboard',
    description: 'Comprehensive analytics with predictive insights and KPI tracking',
    category: 'Analytics',
    progress: 45,
    estimatedCompletion: 'Q2 2025'
  },
  {
    id: 23,
    title: 'Third-party Integrations',
    description: 'API integrations with QuickBooks, Salesforce, and other platforms',
    category: 'Integrations',
    progress: 30,
    estimatedCompletion: 'Q2 2025'
  }
])

const plannedFeatures = ref([
  {
    id: 24,
    title: 'AI-Powered Forecasting',
    description: 'Predictive analytics for project timelines, costs, and resource allocation',
    category: 'AI',
    plannedDate: 'Q3 2025'
  },
  {
    id: 25,
    title: 'Blockchain Smart Contracts',
    description: 'Secure, transparent payment and contract management using blockchain',
    category: 'Blockchain',
    plannedDate: 'Q3 2025'
  },
  {
    id: 26,
    title: 'Advanced Reporting Engine',
    description: 'Custom report builder with data visualization and automated insights',
    category: 'Analytics',
    plannedDate: 'Q4 2025'
  },
  {
    id: 27,
    title: 'Multi-language Support',
    description: 'Internationalization for global markets and multi-lingual users',
    category: 'Core',
    plannedDate: 'Q4 2025'
  },
  {
    id: 28,
    title: 'API Developer Platform',
    description: 'Public API marketplace for third-party developers and integrations',
    category: 'Platform',
    plannedDate: 'Q1 2026'
  }
])

// Computed properties
const popularCategories = computed(() => {
  const categories = featureRequests.value.map(req => req.category)
  return [...new Set(categories)]
})

const highPriorityFeatures = computed(() => {
  return plannedFeatures.value.filter(f => f.priority === 'high')
})

const mediumPriorityFeatures = computed(() => {
  return plannedFeatures.value.filter(f => f.priority === 'medium')
})

// AI-powered roadmap management
const roadmapAI = {
  analyzeFeatureRequests: async (requests: any[]) => {
    try {
      const response = await fetch('/api/ai/roadmap-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          featureRequests: requests,
          currentFeatures: completedFeatures.value,
          industryTrends: true
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI analysis failed:', error)
      return null
    }
  },

  suggestPriorities: async (features: any[]) => {
    try {
      const response = await fetch('/api/ai/feature-prioritization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          features: features,
          userFeedback: true,
          marketDemand: true
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI prioritization failed:', error)
      return null
    }
  },

  generateTimeline: async (features: any[], options: any) => {
    try {
      const response = await fetch('/api/ai/timeline-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          features: features,
          teamCapacity: options.teamCapacity,
          dependencies: options.dependencies,
          resourceAllocation: options.resourceAllocation
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI timeline generation failed:', error)
      return null
    }
  }
}

// Methods
const analyzeFeatureRequests = async () => {
  isAnalyzing.value = true
  
  try {
    const result = await roadmapAI.analyzeFeatureRequests(featureRequests.value)
    if (result && result.success) {
      aiSuggestions.value = result.suggestions || []
      alert('AI analysis completed! Check the suggestions below.')
    } else {
      alert('AI analysis failed. Please try again.')
    }
  } catch (error) {
    console.error('Analysis error:', error)
    alert('Analysis failed. Please try again.')
  } finally {
    isAnalyzing.value = false
  }
}

const generatePriorities = async () => {
  isGeneratingPriorities.value = true
  
  try {
    const allFeatures = [...inProgressFeatures.value, ...plannedFeatures.value]
    const result = await roadmapAI.suggestPriorities(allFeatures)
    if (result && result.success) {
      // Update feature priorities based on AI suggestions
      updateFeaturePriorities(result.priorities)
      alert('AI priorities generated successfully!')
    } else {
      alert('Priority generation failed. Please try again.')
    }
  } catch (error) {
    console.error('Priority generation error:', error)
    alert('Priority generation failed. Please try again.')
  } finally {
    isGeneratingPriorities.value = false
  }
}

const generateTimeline = async () => {
  isGeneratingTimeline.value = true
  
  try {
    const allFeatures = [...inProgressFeatures.value, ...plannedFeatures.value]
    const result = await roadmapAI.generateTimeline(allFeatures, timelineOptions.value)
    if (result && result.success) {
      generatedTimeline.value = result.timeline
      alert('AI timeline generated successfully!')
    } else {
      alert('Timeline generation failed. Please try again.')
    }
  } catch (error) {
    console.error('Timeline generation error:', error)
    alert('Timeline generation failed. Please try again.')
  } finally {
    isGeneratingTimeline.value = false
  }
}

const addToRoadmap = (suggestion: any) => {
  const newFeature = {
    id: Date.now(),
    title: suggestion.title,
    description: suggestion.description,
    category: suggestion.category,
    plannedDate: 'Q2 2025',
    priority: suggestion.priority
  }
  
  plannedFeatures.value.push(newFeature)
  aiSuggestions.value = aiSuggestions.value.filter(s => s.title !== suggestion.title)
  alert('Feature added to roadmap!')
}

const dismissSuggestion = (suggestion: any) => {
  aiSuggestions.value = aiSuggestions.value.filter(s => s.title !== suggestion.title)
}

const updateProgress = (feature: any) => {
  const newProgress = prompt(`Enter new progress for ${feature.title} (0-100):`)
  if (newProgress !== null && !isNaN(Number(newProgress))) {
    feature.progress = Math.min(100, Math.max(0, Number(newProgress)))
  }
}

const markCompleted = (feature: any) => {
  if (confirm(`Mark "${feature.title}" as completed?`)) {
    const completedFeature = {
      ...feature,
      completedDate: 'Q1 2025',
      progress: 100
    }
    completedFeatures.value.push(completedFeature)
    inProgressFeatures.value = inProgressFeatures.value.filter(f => f.id !== feature.id)
  }
}

const moveToInProgress = (feature: any) => {
  if (confirm(`Move "${feature.title}" to In Progress?`)) {
    const inProgressFeature = {
      ...feature,
      progress: 0,
      estimatedCompletion: 'Q2 2025'
    }
    inProgressFeatures.value.push(inProgressFeature)
    plannedFeatures.value = plannedFeatures.value.filter(f => f.id !== feature.id)
  }
}

const editFeature = (feature: any) => {
  const newTitle = prompt('Enter new title:', feature.title)
  const newDescription = prompt('Enter new description:', feature.description)
  
  if (newTitle && newDescription) {
    feature.title = newTitle
    feature.description = newDescription
  }
}

const updateFeaturePriorities = (priorities: any[]) => {
  priorities.forEach(priority => {
    const feature = [...inProgressFeatures.value, ...plannedFeatures.value]
      .find(f => f.title === priority.feature.title)
    if (feature) {
      feature.priority = priority.priority
    }
  })
}
</script>

<style scoped>
.btn-primary-modern {
  @apply inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 transform hover:scale-105;
}

.btn-outline {
  @apply inline-flex items-center px-3 py-1 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors duration-200;
}

.btn-secondary {
  @apply inline-flex items-center px-3 py-1 bg-slate-600 text-slate-300 rounded-lg hover:bg-slate-500 transition-colors duration-200;
}

.btn-success {
  @apply inline-flex items-center px-3 py-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors duration-200;
}
</style>
